import {
  CHART_LABEL_SKIP_LIST,
  EMAIL_ANONYMIZED_SUFFIX,
} from './demo-mode.constants';
import {
  DISTRIBUTOR_NAMES,
  CLIENT_NAMES,
  CAMPUS_NAMES,
  MARKET_NAMES,
  MACHINE_LOCATION_NAMES,
} from './demo-mode-human-names';

type EntityType = 'distributor' | 'market' | 'client' | 'campus' | 'machine';

/** Map for chart-style labels (string[] without entity IDs) - keyed by original string */
const chartLabelsMap = new Map<string, string>();

/** Maps for analytics labels by breakdown dimension - keyed by (entityType, original) */
const labelMapsByEntity: Record<EntityType, Map<string, string>> = {
  distributor: new Map(),
  market: new Map(),
  client: new Map(),
  campus: new Map(),
  machine: new Map(),
};

export type AnonymizeContext = { breakdownBy?: string };

/** Map for anonymizing emails (approvers, createdBy, etc.) */
const emailMap = new Map<string, string>();

function getOrCreateAnonymizedEmail(original: string): string {
  if (!original || typeof original !== 'string' || !original.includes('@')) {
    return original;
  }
  let anonymized = emailMap.get(original);
  if (!anonymized) {
    const index = emailMap.size + 1;
    anonymized = `user${index}${EMAIL_ANONYMIZED_SUFFIX}`;
    emailMap.set(original, anonymized);
  }
  return anonymized;
}

/** Heuristic: "City, ST" format suggests market/location */
function looksLikeLocation(str: string): boolean {
  return /^.+, [A-Za-z]{2}$/.test(str.trim());
}

/** Heuristic: label looks like a date (time series) - don't anonymize */
function looksLikeDate(str: string): boolean {
  const s = str.trim();
  if (!s) return false;
  // ISO date, "Jan 2024", "1/15/2024", "2024-01", "January 2024", etc.
  if (/^\d{4}-\d{2}(-\d{2})?$/.test(s)) return true; // 2024-01 or 2024-01-15
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s)) return true; // 1/15/2024
  if (/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,4}$/i.test(s)) return true; // Jan 2024, Jan 15 2024
  if (/^\d{1,2}\/\d{1,2}$/.test(s)) return true; // 1/15
  return false;
}

function getHumanName(entityType: EntityType, index: number): string {
  const names =
    entityType === 'distributor' ? DISTRIBUTOR_NAMES
    : entityType === 'client' ? CLIENT_NAMES
    : entityType === 'campus' ? CAMPUS_NAMES
    : entityType === 'market' ? MARKET_NAMES
    : entityType === 'machine' ? MACHINE_LOCATION_NAMES
    : [];
  const base = names[(index - 1) % names.length];
  return index > names.length ? `${base} ${Math.floor((index - 1) / names.length) + 1}` : base;
}

function getOrCreateLabelForEntity(original: string, entityType: EntityType): string {
  const map = labelMapsByEntity[entityType];
  const key = original.trim();
  if (!key || CHART_LABEL_SKIP_LIST.some((skip) => key === skip)) return original;
  let anonymized = map.get(key);
  if (!anonymized) {
    const index = map.size + 1;
    anonymized = getHumanName(entityType, index);
    map.set(key, anonymized);
  }
  return anonymized;
}

function getOrCreateChartLabel(original: string): string {
  const trimmed = original.trim();
  if (CHART_LABEL_SKIP_LIST.some((skip) => trimmed === skip || !trimmed)) {
    return original;
  }
  let anonymized = chartLabelsMap.get(original);
  if (!anonymized) {
    const index = chartLabelsMap.size + 1;
    anonymized = looksLikeLocation(original)
      ? getHumanName('market', index)
      : `Label ${index}`;
    chartLabelsMap.set(original, anonymized);
  }
  return anonymized;
}

const ENTITY_CONFIG: Record<
  EntityType,
  { idFields: string[]; nameFields: string[] }
> = {
  distributor: {
    idFields: ['disti_id', 'distiid', 'distiId'],
    nameFields: ['name', 'disti_name', 'distiName'],
  },
  market: {
    idFields: ['market_id', 'marketid', 'marketId'],
    nameFields: ['market_name', 'marketName'],
  },
  client: {
    idFields: ['client_id', 'clientid', 'clientId'],
    nameFields: [
      'company_name',
      'companyName',
      'client_name',
      'clientName',
      'prefix_text',
      'prefix',
      'access_id',
      'accessId',
    ],
  },
  campus: {
    idFields: ['campus_id', 'campusid', 'campusId'],
    nameFields: ['name', 'campus_name', 'campusName'],
  },
  machine: {
    idFields: ['machine_id', 'machineid', 'machineId', 'id'],
    nameFields: ['machine_name', 'machineName', 'serial_no', 'serialNo', 'name'],
  },
};

/** Top-machines API uses plain 'id'; only treat as machine when value looks like a machine serial (avoids drink types) */
function looksLikeMachineId(id: string | number): boolean {
  const s = String(id);
  return /^[A-Z0-9][A-Za-z0-9_-]*$/.test(s) && s.length >= 4;
}

/** Object has inventory-specific fields: name is product label (BEANS, COCOA, etc.), not machine name */
function looksLikeInventoryItem(obj: Record<string, unknown>): boolean {
  return (
    obj.canSize !== undefined ||
    obj.remSize !== undefined ||
    obj.slotNo !== undefined ||
    obj.lowLevel !== undefined ||
    obj.emptyLevel !== undefined ||
    obj.colorBox !== undefined
  );
}

const idMaps: Record<EntityType, Map<string, string>> = {
  distributor: new Map(),
  market: new Map(),
  client: new Map(),
  campus: new Map(),
  machine: new Map(),
};

function getOrCreateGenericName(entityType: EntityType, id: string | number): string {
  const map = idMaps[entityType];
  const key = String(id);
  let name = map.get(key);
  if (!name) {
    const index = map.size + 1;
    name = getHumanName(entityType, index);
    map.set(key, name);
  }
  return name;
}

const clientPrefixMap = new Map<string, string>();

function getOrCreateClientPrefix(id: string | number): string {
  const key = String(id);
  let prefix = clientPrefixMap.get(key);
  if (!prefix) {
    const index = clientPrefixMap.size + 1;
    prefix = `CLT${index}`;
    clientPrefixMap.set(key, prefix);
  }
  return prefix;
}

function detectEntity(obj: Record<string, unknown>): { type: EntityType; id: string | number } | null {
  for (const [entityType, config] of Object.entries(ENTITY_CONFIG) as [EntityType, typeof ENTITY_CONFIG[EntityType]][]) {
    for (const idField of config.idFields) {
      const id = obj[idField];
      if (id != null && (typeof id === 'string' || typeof id === 'number')) {
        if (idField === 'id' && entityType === 'machine' && !looksLikeMachineId(id)) continue;
        return { type: entityType, id };
      }
    }
  }
  return null;
}

/** Detect all entities in an object (used when object has multiple entity types in one row) */
function detectAllEntities(obj: Record<string, unknown>): Array<{ type: EntityType; id: string | number }> {
  const entities: Array<{ type: EntityType; id: string | number }> = [];
  for (const [entityType, config] of Object.entries(ENTITY_CONFIG) as [EntityType, typeof ENTITY_CONFIG[EntityType]][]) {
    for (const idField of config.idFields) {
      const id = obj[idField];
      if (id != null && (typeof id === 'string' || typeof id === 'number')) {
        if (idField === 'id' && entityType === 'machine' && !looksLikeMachineId(id)) continue;
        entities.push({ type: entityType, id });
        break;
      }
    }
  }
  return entities;
}

/** Anonymize a single label string based on context (breakdownBy). Used for labels array and contextualData keys. */
function anonymizeLabel(label: string, context?: AnonymizeContext): string {
  const breakdownBy = context && context.breakdownBy;
  if (breakdownBy === 'drink' || breakdownBy === 'product') return label;
  if (looksLikeDate(label)) return label;
  const entityType: EntityType | null =
    breakdownBy === 'distributor' ? 'distributor'
    : breakdownBy === 'market' ? 'market'
    : breakdownBy === 'client' ? 'client'
    : breakdownBy === 'campus' ? 'campus'
    : breakdownBy === 'machine' ? 'machine'
    : null;
  if (entityType) return getOrCreateLabelForEntity(label, entityType);
  return getOrCreateChartLabel(label);
}

function anonymizeValue(value: unknown, context?: AnonymizeContext): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => anonymizeValue(item, context));
  }

  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    const entity = detectEntity(obj);
    const allEntities = detectAllEntities(obj);
    const result: Record<string, unknown> = {};

    for (const [k, v] of Object.entries(obj)) {
      const lowerKey = k.toLowerCase();

      // Chart-style labels array: anonymize based on breakdownBy (analytics) or generic
      if (lowerKey === 'labels' && Array.isArray(v) && v.every((l) => typeof l === 'string')) {
        result[k] = (v as string[]).map((l) => anonymizeLabel(l, context));
        continue;
      }

      // contextualData: keys are label strings, rekey with same anonymization as labels
      if (lowerKey === 'contextualdata' && v && typeof v === 'object' && !Array.isArray(v)) {
        const newContextual: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(v)) {
          const newKey = anonymizeLabel(key, context);
          newContextual[newKey] = anonymizeValue(val, context);
        }
        result[k] = newContextual;
        continue;
      }

      // Check if this key is a name field for any entity in the object (handles multi-entity rows like campus inventory)
      let matchingEntity: { type: EntityType; id: string | number } | null = null;
      for (const e of allEntities) {
        if (ENTITY_CONFIG[e.type].nameFields.some((nf) => nf.toLowerCase() === lowerKey)) {
          matchingEntity = e;
          break;
        }
      }
      if (!matchingEntity && entity && ENTITY_CONFIG[entity.type].nameFields.some((nf) => nf.toLowerCase() === lowerKey)) {
        matchingEntity = entity;
      }

      if (matchingEntity) {
        // Inventory items: name is product label (BEANS, COCOA, etc.), not machine/entity name - do not anonymize
        if (lowerKey === 'name' && looksLikeInventoryItem(obj)) {
          result[k] = v;
        } else if (lowerKey === 'prefix_text' || lowerKey === 'prefix') {
          result[k] = getOrCreateClientPrefix(matchingEntity.id);
        } else if (lowerKey === 'access_id' || lowerKey === 'accessid') {
          result[k] = getOrCreateClientPrefix(matchingEntity.id) + 'accessid';
        } else {
          result[k] = getOrCreateGenericName(matchingEntity.type, matchingEntity.id);
        }
      } else if (lowerKey === 'account' && typeof v === 'string' && v.trim()) {
        result[k] = getOrCreateChartLabel(v);
      } else if (lowerKey === 'machine' && typeof v === 'string' && looksLikeMachineId(v)) {
        // Analytics stockout rows/heatmap: machine field holds machine ID string
        result[k] = getOrCreateGenericName('machine', v);
      } else if ((lowerKey === 'createdby' || lowerKey === 'updatedby') && typeof v === 'string') {
        result[k] = getOrCreateAnonymizedEmail(v);
      } else if (lowerKey === 'approvers' && Array.isArray(v)) {
        result[k] = (v as string[]).map((e) =>
          typeof e === 'string' ? getOrCreateAnonymizedEmail(e) : anonymizeValue(e, context)
        );
      } else {
        result[k] = anonymizeValue(v, context);
      }
    }

    // Machine: add display IDs for UI. For camelCase/plain id (dashboard widgets): replace in place so components need no changes.
    // For snake_case machine_id (machine list): keep for routing, add machine_id_display for display.
    const machineEntity = allEntities.find((e) => e.type === 'machine');
    if (machineEntity) {
      const displayId = getOrCreateGenericName('machine', machineEntity.id);
      // machine_id: keep for routing (links), add _display for UI
      result.machine_id_display = displayId;
      // machineId, id: replace in place so Fleet Health, Top Machines, Recent Activities display anonymized without component changes
      if (obj.machineId !== undefined) result.machineId = displayId;
      if (obj.id !== undefined) result.id = displayId;
    }

    return result;
  }

  return value;
}

/**
 * Recursively anonymizes name fields in API response data.
 * Uses deterministic mapping so the same entity ID always maps to the same generic name.
 * @param context.breakdownBy - For analytics: 'drink'|'product' (no anonymization), 'distributor'|'market'|'client'|'campus'|'machine' (entity-specific), or dates (no anonymization)
 */
export function anonymize<T>(data: T, context?: AnonymizeContext): T {
  if (data === null || data === undefined) {
    return data;
  }
  return anonymizeValue(data, context) as T;
}
